/**
 * Publish what a component shows into the screen snapshot (plan 1968 F13).
 *
 * - `useScreenSection(id, section)`: a ready section (KPI card, chart summary). Several
 *   components may publish under the same `id` — each instance is its own publisher and
 *   the parts merge into one section at capture time (two KPI cards never overwrite each
 *   other). A new object with the same content does not re-publish (compared by JSON).
 * - `useScreenCapture(id, capture, { version })`: a function that READS the section when
 *   the reporter captures (DOM tables, detail fields) — nothing is computed while the AI
 *   panel is closed. Bump `version` when the content changed so an open panel re-posts.
 *
 * Both: `null` clears, unmount clears, and the part only counts on the pathname it was
 * published on (pass `path` from useLocation when the component outlives navigations).
 * Display strings exactly as shown; never unsaved edits, tokens or hidden tabs.
 */

import { useEffect, useId, useMemo, useRef } from "react";
import { pageContextStore } from "./pageContextStore";
import type { ScreenSection, ScreenSectionInput } from "./types";

export type { ScreenSectionInput };

function usePublisherId(id: string): string {
  return `${id}#${useId()}`;
}

function useClearOnUnmount(publisherId: string, id: string): void {
  useEffect(() => {
    return () => pageContextStore.getState().publishScreenSection(publisherId, id, null);
  }, [publisherId, id]);
}

export function useScreenSection(id: string, section: ScreenSectionInput | null, path?: string): void {
  const publisherId = usePublisherId(id);
  const serialized = useMemo(() => {
    if (!section) return null;
    try {
      return JSON.stringify(section);
    } catch {
      return null;
    }
  }, [section]);

  useEffect(() => {
    const parsed = serialized === null ? null : ({ ...JSON.parse(serialized), id } as ScreenSection);
    pageContextStore.getState().publishScreenSection(publisherId, id, parsed, path);
  }, [publisherId, id, serialized, path]);

  useClearOnUnmount(publisherId, id);
}

export function useScreenCapture(
  id: string,
  capture: (() => ScreenSectionInput | null) | null,
  options: { path?: string; version?: unknown } = {},
): void {
  const publisherId = usePublisherId(id);
  const captureRef = useRef(capture);
  captureRef.current = capture;
  const active = capture !== null;
  const { path, version } = options;

  useEffect(() => {
    const read = () => {
      const section = captureRef.current?.();
      return section ? { ...section, id } : null;
    };
    pageContextStore.getState().publishScreenSection(publisherId, id, active ? read : null, path);
  }, [publisherId, id, active, path, version]);

  useClearOnUnmount(publisherId, id);
}
